import React, { useContext, useEffect, useRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { calculateYAxisConfig } from '../../helpers/chartHelpers';
import { ToolContext } from '../../../../../core/context/ToolContext';
import { getDateRangeByFilter, shouldFetchForFilter } from '../../helpers/dateRangeHelper';
import { SnakeLoader } from '../../../../../core/loader/SnakeLoader';
import ChartFilter from '../../helpers/chartFilter';
import { fetchAllPosts, fetchInstagramInsights, generateLinkClicksData } from '../../helpers/metricHelpers';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const getAxisConfig = (isYAxis = false) => ({
    grid: {
        display: isYAxis,
        color: isYAxis ? 'rgba(0, 0, 0, 0.07)' : false,
        drawBorder: false,
        ...(isYAxis && { lineWidth: 1, borderDash: [5, 5] })
    },
    ticks: {
        color: '#666',
        font: {
            size: 12,
        },
        ...(isYAxis && {
            stepSize: 1,
            callback: (value) => value
        })
    },
    border: {
        display: false,
    }
});

export const LinkClicksChart = () => {
    const chartRef = useRef(null);
    const { social_media_insight_state, tool_state } = useContext(ToolContext)
    const { userId, __SESSION } = tool_state;
    const platform = social_media_insight_state.connectedPlatform.data;
    const listView = async () => {
        if (!(platform != '' && !social_media_insight_state.showProfilesModal.data && (platform !== 'facebook' || (platform === 'facebook' && social_media_insight_state.accessToken.data)))) return;
        try {
            social_media_insight_state.linkClicksLoading.set(true);
            const initialUrl = `${social_media_insight_state.selectedProfileId.data}/posts?fields=id,created_time,insights.metric(post_clicks_by_type)&limit=100`;
            const dateRange = getDateRangeByFilter(social_media_insight_state.linkClicksFilter.data, social_media_insight_state.linkClicksCustomRange.data);
            let posts = [];
            if (platform == 'facebook') {
                const { allPosts } = await fetchAllPosts(initialUrl, social_media_insight_state.accessToken.data);
                posts = allPosts;
            } else {
                const { allPosts } = await fetchInstagramInsights({
                    accountId: social_media_insight_state.instagramAccountId.data,
                    accessToken: social_media_insight_state.instagramAccessToken.data,
                    metric_name: 'website_clicks',
                    start_date: dateRange.start_date_unix,
                    end_date: dateRange.end_date_unix,
                });
                posts = allPosts;
            }
            const linkClicksData = generateLinkClicksData(dateRange.start_date, dateRange.end_date, posts);
            social_media_insight_state.linkClicksData.set(linkClicksData);
        } catch (error) {
            social_media_insight_state.linkClicksData.set([]);
        } finally {
            social_media_insight_state.linkClicksLoading.set(false)
            social_media_insight_state.insightLoading.set(false)
        }
    }

    useEffect(() => {
        if (shouldFetchForFilter(social_media_insight_state.linkClicksFilter.data, social_media_insight_state.linkClicksCustomRange.data)) {
            listView();
        }
    }, [social_media_insight_state.linkClicksFilter.data, social_media_insight_state.linkClicksCustomRange.data, social_media_insight_state.urlCode.data, platform]);

    const linkClicksData = social_media_insight_state.linkClicksData.data || [];
    const labels = linkClicksData?.map(item => item?.date);
    const linkClicks = linkClicksData?.map(item => item?.value);
    const yAxisConfig = calculateYAxisConfig(linkClicks);

    const data = {
        labels,
        datasets: [
            {
                type: 'bar',
                label: 'Link Clicks',
                data: linkClicks,
                backgroundColor: function (context) {
                    const index = context.dataIndex;
                    const chart = context.chart;
                    const activeElements = chart.getActiveElements();
                    const isActive = activeElements.some(
                        (el) => el.datasetIndex === context.datasetIndex && el.index === index
                    );
                    return isActive ? '#344BFD' : '#E6E8EC';
                },
                barThickness: 30,
                barPercentage: 0.5,
                categoryPercentage: 0.7,
            }
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: getAxisConfig(),
            y: {
                ...getAxisConfig(true),
                beginAtZero: true,
                max: yAxisConfig.max,
                ticks: {
                    ...getAxisConfig(true).ticks,
                    stepSize: yAxisConfig.stepSize,
                    callback: yAxisConfig.ticks.callback
                }
            }
        },
        plugins: {
            tooltip: {
                backgroundColor: '#061D22',
                padding: 12,
                cornerRadius: 18,
                displayColors: false,
                titleFont: {
                    size: 0
                },
                bodyFont: {
                    size: 14,
                    weight: '600'
                },
                callbacks: {
                    title: () => '',
                    label: (context) => {
                        if (context.dataset.label === 'Link Clicks') {
                            return `${context.parsed.y} clicks`;
                        }
                        return context.parsed.y;
                    },
                },
                position: 'nearest',
                intersect: false,
                mode: 'index',
            },
            legend: {
                display: false,
            },
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
        layout: {
            padding: {
                left: 0,
                right: 0
            }
        }
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box fontSize={18} fontWeight={700} mb={2}>
                    Link Clicks
                </Box>
                <Box display="flex" gap={2}>
                    <ChartFilter
                        filterValue={social_media_insight_state.linkClicksFilter.data}
                        setFilterValue={social_media_insight_state.linkClicksFilter.set}
                        customRange={social_media_insight_state.linkClicksCustomRange.data}
                        setCustomRange={social_media_insight_state.linkClicksCustomRange.set}
                        // minDate={dayjs().subtract(2, 'year').toDate()}
                        maxDate={dayjs().toDate()}
                    />
                </Box>
            </Box>
            <div style={{ height: '300px', width: '100%' }}>
                {social_media_insight_state.linkClicksLoading.data ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                        <SnakeLoader size="1rem" bg="#11783C" distance="0.3rem" />
                    </ Box>
                ) :
                    (linkClicksData.length > 0 && linkClicksData.find(m => m.value > 0)) ? (
                        <Bar ref={chartRef} data={data} options={options} />
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                            No data available
                        </Box>
                    )}
            </div>
        </Box>
    );
};